// Correo de la Consola sobre el correo del motor (D27): buzones IMAP/SMTP conectados, mensajes sincronizados y envio.
// Tipos de buzon: personal (lo conecta cada persona en Mi perfil; es solo suyo, ni el administrador lo lee) y de
// grupo (helpdesk@, ventas@: un administrador lo asocia a un grupo y lo leen sus miembros). El correo se lee solo por
// el servicio de correo, que aplica esa regla (D38). El SMTP global del servidor es solo para notificaciones del
// sistema y no aparece aqui.
import { archivoConSesion, metadataSesion, request } from './http';

import { carpetaSeSincroniza, type MensajeBase, textoAHtml } from '../lib/correo';
import { markdownAHtml } from '../lib/markdownIA';

export type Seguridad = 'SSL_TLS' | 'STARTTLS' | 'NONE';
export type Servidor = { host: string; puerto: number; seguridad: Seguridad };
export type DatosBuzon = { correo: string; nombre: string; usuario: string; clave: string; imap: Servidor; smtp: Servidor };

export type Buzon = {
  id: string;
  correo: string;
  nombre: string;
  compartido: boolean;
  fallo: string | null;
  canal: { id: string; estado: string; etapa: string; sincronizado: string | null; activo: boolean; visibilidad: string } | null;
};

type RawCuenta = { id: string; handle: string; name: string | null; provider: string; visibility: string; authFailedAt: string | null; authFailedReason: string | null; archivedAt: string | null };
type RawCanal = { id: string; syncStatus: string; syncStage: string; syncedAt: string | null; isSyncEnabled: boolean; visibility: string };

export async function buzones(): Promise<Buzon[]> {
  const { myConnectedAccounts } = await metadataSesion<{ myConnectedAccounts: RawCuenta[] }>('{ myConnectedAccounts { id handle name provider visibility authFailedAt authFailedReason archivedAt } }');
  const cuentas = myConnectedAccounts.filter((c) => c.provider.toUpperCase() === 'IMAP_SMTP_CALDAV' && !c.archivedAt);
  return Promise.all(
    cuentas.map(async (c) => {
      const { myMessageChannels } = await metadataSesion<{ myMessageChannels: RawCanal[] }>(
        'query($id:UUID!){ myMessageChannels(connectedAccountId:$id) { id syncStatus syncStage syncedAt isSyncEnabled visibility } }',
        { id: c.id },
      ).catch(() => ({ myMessageChannels: [] as RawCanal[] }));
      const k = myMessageChannels[0];
      return {
        id: c.id,
        correo: c.handle,
        nombre: c.name || c.handle,
        compartido: c.visibility === 'workspace',
        fallo: c.authFailedAt ? c.authFailedReason || 'El servidor rechazó el usuario o la clave' : null,
        canal: k ? { id: k.id, estado: k.syncStatus, etapa: k.syncStage, sincronizado: k.syncedAt, activo: k.isSyncEnabled, visibilidad: k.visibility } : null,
      };
    }),
  );
}

const servidor = (s: Servidor, usuario: string, clave: string) => ({ host: s.host.trim(), port: s.puerto, username: usuario.trim(), password: clave, connectionSecurity: s.seguridad });

// El motor prueba IMAP y SMTP antes de guardar; si algo falla, devuelve el error y no guarda nada.
export async function conectarBuzon(d: DatosBuzon) {
  const { saveImapSmtpCaldavAccount } = await metadataSesion<{ saveImapSmtpCaldavAccount: { connectedAccountId?: string } }>(
    'mutation($h:String!,$p:EmailAccountConnectionParameters!){ saveImapSmtpCaldavAccount(handle:$h, connectionParameters:$p){ connectedAccountId } }',
    { h: d.correo.trim().toLowerCase(), p: { name: d.nombre.trim() || null, IMAP: servidor(d.imap, d.usuario, d.clave), SMTP: servidor(d.smtp, d.usuario, d.clave) } },
  );
  const id = saveImapSmtpCaldavAccount.connectedAccountId;
  if (id) await empezarSincronizacion(id);
  return id;
}

// Lo que la pantalla del motor pide despues de conectar: que carpetas traer (entrada y enviados) y arrancar la
// sincronizacion. Sin esto el buzon queda "pendiente de configurar". El buzon queda privado (METADATA): solo pasa a
// compartido cuando un administrador lo vuelve buzon de grupo (servicio de correo, /compartir).
export async function empezarSincronizacion(cuentaId: string) {
  const { myMessageChannels } = await metadataSesion<{ myMessageChannels: RawCanal[] }>('query($id:UUID!){ myMessageChannels(connectedAccountId:$id) { id visibility syncStage } }', { id: cuentaId });
  for (const k of myMessageChannels) {
    await metadataSesion('mutation($i:UpdateMessageChannelInput!){ updateMessageChannel(input:$i){ id } }', {
      // Sin contactos automaticos (D81, como helpdesk@ y jdrodriguez@): un buzon con anos de historia o un Gmail
      // personal llenaria el CRM de contactos.
      i: { id: k.id, update: { visibility: k.visibility === 'SHARE_EVERYTHING' ? 'SHARE_EVERYTHING' : 'METADATA', messageFolderImportPolicy: 'SELECTED_FOLDERS', contactAutoCreationPolicy: 'NONE' } },
    });
    const { myMessageFolders } = await metadataSesion<{ myMessageFolders: { id: string; name: string; isSynced: boolean }[] }>(
      'query($id:UUID){ myMessageFolders(messageChannelId:$id){ id name isSynced } }',
      { id: k.id },
    );
    const si = myMessageFolders.filter((c) => carpetaSeSincroniza(c.name)).map((c) => c.id);
    const no = myMessageFolders.filter((c) => !carpetaSeSincroniza(c.name)).map((c) => c.id);
    const carpetas = 'mutation($i:UpdateMessageFoldersInput!){ updateMessageFolders(input:$i){ id } }';
    if (si.length) await metadataSesion(carpetas, { i: { ids: si, update: { isSynced: true } } });
    if (no.length) await metadataSesion(carpetas, { i: { ids: no, update: { isSynced: false } } });
  }
  await metadataSesion('mutation($id:UUID!){ startChannelSync(connectedAccountId:$id){ success } }', { id: cuentaId });
}

export const quitarBuzon = (id: string) => metadataSesion('mutation($id:UUID!){ deleteConnectedAccount(id:$id){ id } }', { id });

/* ---------------- mensajes ---------------- */

export type Mensaje = MensajeBase & { asunto: string; texto: string; headerMessageId: string | null };

// Correos de un periodo (reporte). Tope para no saturar el servidor: el reporte avisa si se llego al tope.
export const TOPE_REPORTE = 1500;
export const mensajesEntre = async (desde: Date, hasta: Date) =>
  (await request<{ mensajes: Mensaje[] }>('/correo/api/reporte', { query: { desde: desde.toISOString(), hasta: hasta.toISOString() } })).mensajes;

// Una conversacion completa, solo con los correos de buzones que esta persona puede leer.
// Sugerencias de destinatarios (D60): equipo, contactos del CRM y personas con quienes se ha escrito (buzones legibles).
export type ContactoSugerido = { correo: string; nombre: string; origen: 'equipo' | 'contacto' | 'correo' };
export const buscarContactos = async (q: string) => (await request<{ contactos: ContactoSugerido[] }>('/correo/api/contactos', { query: { q, limite: 8 } })).contactos;

export const mensajesDeHilo = async (hiloId: string) => (await request<{ mensajes: Mensaje[] }>(`/correo/api/hilo/${hiloId}`)).mensajes;

// Correos de los hilos de un caso que yo no puedo leer (buzon personal de otro): solo quien y cuando (capa 4, "nada
// invisible"). Si el servicio aun no tiene la ruta, ninguno.
export type CorreoOculto = { messageId: string | null; fecha: string; nombre: string; correo: string };
export const ocultosDeCaso = async (casoId: string) =>
  (await request<{ ocultos: CorreoOculto[] }>(`/correo/api/caso/${casoId}/ocultos`).catch((e: { status?: number }) => {
    if (e?.status === 404) return { ocultos: [] };
    throw e;
  })).ocultos;

export type Adjunto = { id: string; name: string; tamano: number };
export type Envio = { cuentaId: string; para: string[]; cc?: string[]; cco?: string[]; asunto: string; texto: string; firma?: string; responderA?: string | null; adjuntos?: Adjunto[]; html?: string };

export async function enviar(e: Envio) {
  // html: cuerpo ya armado (correos de automatizaciones). Si no, el texto pasa a HTML con formato (negrita, listas y
  // tablas de Markdown, como las respuestas de AMIX, D98) y la firma se agrega como siempre.
  const cuerpo = e.html ?? `${markdownAHtml(e.texto)}${e.firma ? textoAHtml(`--\n${e.firma.trim()}`) : ''}`;
  const { sendEmail } = await metadataSesion<{ sendEmail: { success: boolean; error: string | null; messageThreadId: string | null } }>(
    'mutation($i:SendEmailInput!){ sendEmail(input:$i){ success error messageThreadId } }',
    {
      i: {
        connectedAccountId: e.cuentaId,
        to: e.para.join(', '),
        cc: (e.cc ?? []).join(', ') || undefined,
        bcc: (e.cco ?? []).join(', ') || undefined,
        subject: e.asunto.trim(),
        body: cuerpo,
        inReplyTo: e.responderA || undefined,
        files: e.adjuntos?.length ? e.adjuntos.map(({ id, name }) => ({ id, name })) : undefined,
      },
    },
  );
  if (!sendEmail.success) throw new Error(sendEmail.error || 'No se pudo enviar el correo');
  return sendEmail.messageThreadId;
}

// Adjuntos: el motor da una direccion de subida, se sube el archivo y se confirma; al enviar se pasa su id.
export const MAX_ADJUNTOS_MB = 20;
export async function subirAdjunto(archivo: File): Promise<Adjunto> {
  const { createFileUpload: t } = await metadataSesion<{ createFileUpload: { fileId: string; uploadUrl: string; contentType: string | null } }>(
    'mutation($n:String!,$s:Float!){ createFileUpload(filename:$n, size:$s, fileFolder:EmailAttachment){ fileId uploadUrl contentType } }',
    { n: archivo.name, s: archivo.size },
  );
  const destino = new URL(t.uploadUrl, location.origin).toString();
  const r = await fetch(destino, { method: 'PUT', body: archivo, headers: { 'Content-Type': t.contentType || archivo.type || 'application/octet-stream' } });
  if (!r.ok) throw new Error(`No se pudo subir ${archivo.name} (${r.status}).`);
  await metadataSesion('mutation($id:String!){ completeFileUpload(fileId:$id){ id } }', { id: t.fileId });
  return { id: t.fileId, name: archivo.name, tamano: archivo.size };
}

// Adjuntos de un correo recibido (los trae el servicio de correo desde el buzon).
export type AdjuntoRecibido = { parte: string; nombre: string; tipo: string; tamano: number; enLinea: boolean; cid?: string | null };
export const adjuntosDe = async (mensajeId: string) => (await request<{ adjuntos: AdjuntoRecibido[] }>(`/correo/api/adjuntos/${mensajeId}`)).adjuntos;
// Texto completo de un correo del buzon, con lo citado y lo reenviado que el motor descarta (D43).
export const textoCompletoDe = async (mensajeId: string) => (await request<{ texto: string }>(`/correo/api/completo/${mensajeId}`)).texto;

export async function archivoAdjunto(mensajeId: string, parte: string) {
  return archivoConSesion(`/correo/api/adjuntos/${mensajeId}/${parte}`);
}

// Servidores conocidos: se llenan solos al escribir el correo.
export const PROVEEDORES: { dominio: RegExp; nombre: string; imap: Servidor; smtp: Servidor }[] = [
  {
    dominio: /^(xmartek\.com|.*siteprotect\.com)$/i,
    nombre: 'Web.com / SiteProtect',
    imap: { host: 'imap.siteprotect.com', puerto: 993, seguridad: 'SSL_TLS' },
    smtp: { host: 'smtp.siteprotect.com', puerto: 465, seguridad: 'SSL_TLS' },
  },
  {
    dominio: /^(gmail|googlemail)\.com$/i,
    nombre: 'Gmail (con contraseña de aplicación)',
    imap: { host: 'imap.gmail.com', puerto: 993, seguridad: 'SSL_TLS' },
    smtp: { host: 'smtp.gmail.com', puerto: 465, seguridad: 'SSL_TLS' },
  },
  {
    dominio: /^(outlook|hotmail|live)\.com$/i,
    nombre: 'Outlook.com',
    imap: { host: 'outlook.office365.com', puerto: 993, seguridad: 'SSL_TLS' },
    smtp: { host: 'smtp-mail.outlook.com', puerto: 587, seguridad: 'STARTTLS' },
  },
];

// Buzon de grupo: el servicio de correo lo marca como de toda la empresa (o lo devuelve a personal). Solo admin.
export const compartirBuzon = (cuentaId: string, compartir: boolean) => request<{ cuentaId: string; compartido: boolean }>('/correo/api/compartir', { method: 'POST', body: { cuentaId, compartir } });

/* ---------------- conversaciones por buzon (D33) ---------------- */

// Una fila de la lista: el ultimo mensaje del hilo y cuantos tiene. El hilo completo se trae al abrirlo.
export type HiloLista = { id: string; cantidad: number; archivadoPorDefecto: boolean; ultimo: Mensaje; mensajes: Mensaje[] };
type RespuestaHilos = { hilos: Omit<HiloLista, 'mensajes'>[]; mas: boolean };
export type VistaServidor = 'entrada' | 'archivados';
export const HILOS_POR_PAGINA = 150;

// Conversaciones de varios buzones juntas, de la mas reciente a la mas antigua. Cada buzon se pagina por fecha; para no
// saltarse ninguna, la pagina corta en el hilo mas nuevo entre los ultimos de los buzones que tienen mas.
export async function hilosDeBuzones(cuentas: string[], o: { vista: VistaServidor; antes?: string; q?: string }) {
  const rs = await Promise.all(
    cuentas.map(async (cuenta) => ({ cuenta, r: await request<RespuestaHilos>('/correo/api/hilos', { query: { cuenta, limite: HILOS_POR_PAGINA, antes: o.antes, q: o.q, vista: o.vista } }) })),
  );
  const cortes = rs.filter((x) => x.r.mas && x.r.hilos.length).map((x) => x.r.hilos[x.r.hilos.length - 1].ultimo.receivedAt);
  const corte = cortes.length ? cortes.sort().at(-1) : undefined;
  const vistos = new Set<string>();
  const hilos: HiloLista[] = [];
  for (const h of rs.flatMap((x) => x.r.hilos).sort((a, b) => b.ultimo.receivedAt.localeCompare(a.ultimo.receivedAt))) {
    if (corte && h.ultimo.receivedAt < corte) continue;
    if (vistos.has(h.id)) continue;
    vistos.add(h.id);
    hilos.push({ ...h, mensajes: [h.ultimo] });
  }
  return { hilos, siguiente: corte };
}
